import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation, useParams } from 'react-router-dom';
import { createTheme } from "@mui/material/styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { styled } from "@mui/material/styles";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import {
  Box,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  ThemeProvider,
  CardHeader,
  Breadcrumbs,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { putLead } from '../API/leadAPI';
import { getSalesRep } from '../API/salesRepAPI';
import { getLead } from '../API/leadAPI';
import { getJobOrder } from '../API/jobOrderAPI';
import { getSalesReps } from '../API/salesRepAPI';
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  // margin: theme.spacing(4, 0),
  opacity: 0.8,
}));


function EditLead() {
  const { id } = useParams();
  const [salesRep, setSalesRep] = useState(null);
  const [jobOrder, setJobOrder] = useState(null);
  const [showJobOrder, setShowJobOrder] = useState(false);
  const [lead, setLead] = useState({})

  const competencies = ["General Pest Control", "Termite Control", "QPMS", "Disinfection", "Basic Safety"];
  const specializationGPC = [
    "Rodent",
    "Baiting",
    "Cockroach",
    "Fly Control",
    "Ant Control",
    "Larviciding",
  ];

  const specializationTermite = [
    "Compre",
    "AG",
    "IG",
    "IC",
    "Spraying",
    "Soil Poisoning",
  ];

  // New state variables for loading and flash messages
  const [isLoading, setIsLoading] = useState(false);
  const [flashMessage, setFlashMessage] = useState();
  const [salesReps, setSalesReps] = useState([]); // State to hold sales reps
  const [selectedSalesRep, setSelectedSalesRep] = useState(''); // Initialize with current sales rep
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchLead = async () => {
      setIsLoading(true);
      try {
        // Fetch lead by ID
        const leadData = await getLead(id);
        if (leadData) {
          setLead(leadData);

          // Fetch corresponding sales rep if sales_rep_id exists
          if (leadData.sales_rep_id) {
            const rep = await getSalesRep(leadData.sales_rep_id);
            setSalesRep(rep);
            setSelectedSalesRep(leadData.sales_rep_id)
          }

          // Fetch job order if job_order_id exists
          if (leadData.job_order_id) {
            const jobOrderData = await getJobOrder(leadData.job_order_id);
            if (jobOrderData) {
              setJobOrder(jobOrderData);
              setShowJobOrder(true);
            }
          }
        } else {
          setFlashMessage('Lead not found.');
          navigate('/leads'); // Redirect if lead doesn't exist
        }
      } catch (error) {
        console.error('Failed to fetch lead:', error);
        setFlashMessage('An error occurred while fetching the lead.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLead();

    async function fetchSalesReps() {
      const reps = await getSalesReps();
      if (reps) {
        setSalesReps(reps);
      }
    }
    fetchSalesReps();
  }, [id]);



  const handleLeadChange = (field, value) => {
    setLead((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSalesRepChange = (event) => {
    setSelectedSalesRep(event.target.value);
    setLead((prev) => ({
      ...prev,
      sales_rep_id: event.target.value
    }));
  };



  async function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true); // Set loading to true
    try {
      // Deconstruct and exclude client and client_id from lead
      const { id, client, client_id, timestamp_create, timestamp_update, user_create, user_update, ...otherFields } = lead;

      // Call the API with the updated lead (without client and client_id)
      await putLead(id, otherFields);

      setFlashMessage('Lead updated successfully');
      // window.location.reload();  // Reloads the page after submission (if needed)
    } catch (error) {
      console.error('Error:', error);
      setFlashMessage('Error updating lead. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }



  return (
    <BaseFormWrapper
      titleIcon={<TrackChangesIcon />}
      title="Sales"
      subTitle="Manage Leads"
      subTitleRoute="/manage-leads"
      formTitle="Edit Lead"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >

      <form onSubmit={handleSubmit} className="lead-form">
        <Grid container spacing={3}>
          {/* Client Information Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Client Information
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={4}>
                    <TextField
                      label="Client Name"
                      name="clientName"
                      value={lead?.client?.name || ''}
                      fullWidth
                      multiline
                      disabled
                    />
                  </Grid>

                  {lead?.client?.addresses?.map((clientAddress, index) => (
                    <Grid item xs={12} key={index}>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <TextField
                            label="House/Unit Street"
                            name={`house_unit_street ${index + 1}`}
                            value={clientAddress.house_unit_street || ''}
                            disabled
                            fullWidth
                            multiline
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            label="Barangay"
                            value={clientAddress.barangay || ''}
                            fullWidth
                            disabled
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            label="City"
                            name={`city ${index + 1}`}
                            value={clientAddress.city || ''}
                            fullWidth
                            disabled
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <TextField
                            label="Zip Code"
                            name={`zipcode ${index + 1}`}
                            value={clientAddress.zipcode || ''}
                            fullWidth
                            disabled
                          />
                        </Grid>

                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Person Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Contact Person
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {lead?.client?.contacts?.map((contact, index) => (
                  <Grid item xs={12} key={index}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Grid container spacing={3}>
                          <Grid item xs={3}>
                            <TextField
                              label="First Name"
                              fullWidth
                              value={contact.firstname || ''}
                              disabled
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <TextField
                              label="Last Name"
                              fullWidth
                              value={contact.lastname || ''}
                              disabled
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {contact?.numbers?.map((number, numberIndex) => (
                        <React.Fragment key={numberIndex}>
                          <Grid item xs={3}>
                            <TextField
                              label="Contact Type"
                              fullWidth
                              value={
                                number?.type === 'mobile'
                                  ? 'Mobile'
                                  : number?.type === 'landline'
                                    ? 'Landline'
                                    : ''
                              }
                              disabled
                            />
                          </Grid>

                          <Grid item xs={3}>
                            <TextField
                              label="Contact Number"
                              fullWidth
                              value={number?.number || ''}
                              disabled
                            />
                          </Grid>

                        </React.Fragment>
                      ))}

                      {contact?.emails?.map((email, emailIndex) => (
                        <React.Fragment key={emailIndex}>
                          <Grid item xs={6}>
                            <TextField
                              label="Email Address"
                              fullWidth
                              value={email?.email || ''}
                              disabled
                            />
                          </Grid>
                        </React.Fragment>
                      ))}

                    </Grid>
                  </Grid>
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Client Classifications
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {/* Category Field */}
                    <Grid item xs={2}>
                      <TextField
                        label="Category"
                        value={lead?.client?.category || ""}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                        sx={{ fontSize: "0.875rem" }}
                        disabled
                      />
                    </Grid>
                    {/* Parent Client Field */}
                    <Grid item xs={4}>
                      <TextField
                        label="Parent Client"
                        value={lead?.client?.parent_client?.name || ""}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                        sx={{
                          "& .MuiInputBase-input": { fontSize: "0.875rem" },
                          "& .MuiFormLabel-root": { fontSize: "0.875rem" },
                        }}
                        disabled
                      />
                    </Grid>
                    {/* Service Types */}
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        sx={{
                          paddingBottom: "0.5rem",
                          fontWeight: "normal",
                          textTransform: "none",
                          fontSize: "0.875rem",
                          color: "gray",
                        }}
                      >
                        Service Type
                      </Typography>
                      {competencies.map((option) => {
                        const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                        return (
                          <FormControlLabel
                            key={formattedOption}
                            control={
                              <Checkbox
                                name={formattedOption}
                                checked={
                                  Array.isArray(lead?.client?.service_types) &&
                                  lead.client.service_types.includes(formattedOption)
                                }
                              />
                            }
                            label={option}
                            disabled
                          />
                        );
                      })}
                    </Grid>
                    {/* Location Type Field */}
                    <Grid item xs={2}>
                      <TextField
                        label="Location Type"
                        value={
                          lead?.client?.location_type
                            ? lead.client.location_type.charAt(0).toUpperCase() + lead.client.location_type.slice(1).toLowerCase()
                            : ""
                        }
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                        sx={{ fontSize: "0.875rem" }}
                        disabled
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>


          {/* Sales Information Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Sales Information
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={4}>
                        <TextField
                          label="Source Lead / Referred by"
                          name="sourceLeadreferredBy"
                          value={lead?.source_lead_referred_by || ''}  // Use optional chaining with fallback
                          onChange={(e) => handleLeadChange('source_lead_referred_by', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Payment Ref"
                          name="paymentRef"
                          value={lead?.payment_ref ?? ''}  // Use optional chaining with fallback
                          onChange={(e) => handleLeadChange('payment_ref', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Frequency of Service"
                          name="frequencyOfService"
                          value={lead?.frequency_of_service ?? ''}  // Fallback to empty string
                          onChange={(e) => handleLeadChange('frequency_of_service', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={4}>
                        <FormControl fullWidth>
                          <InputLabel id="sales-rep-label">Sales Representative</InputLabel>
                          <Select
                            labelId="sales-rep-label"
                            value={selectedSalesRep ?? ''}  // Prevent undefined state
                            onChange={handleSalesRepChange}
                            label="Sales Representative"
                          >
                            {salesReps.map((rep) => (
                              <MenuItem key={rep.id} value={rep.id}>
                                {`${rep.firstname} ${rep.lastname} - ${rep.branch}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={2}>
                        <FormControl fullWidth>
                          <InputLabel id="status-label">Status</InputLabel>
                          <Select
                            labelId="status-label"
                            value={lead?.status ?? ''}  // Use nullish coalescing to handle undefined
                            onChange={(e) => handleLeadChange('status', e.target.value)}
                            label="Status"
                          >
                            <MenuItem value="open">Open</MenuItem>
                            <MenuItem value="contacted">Contacted</MenuItem>
                            <MenuItem value="ocular">Ocular</MenuItem>
                            <MenuItem value="closed">Closed</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Special Notes
                  </Typography>
                }
              />
              <CardContent>
                <Grid item xs={12}>
                  <TextField
                    label="Special Notes"
                    name="specialNotes"
                    value={lead?.special_notes ?? ''}  // Safe access to prevent undefined
                    onChange={(e) => handleLeadChange('special_notes', e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                  />
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Order Section */}
          {showJobOrder && ( // Conditionally render Job Order fields
            <>
              <Grid
                item
                xs={12}
                md={12}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Card
                  variant="outlined"
                  sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                >
                  <CardHeader
                    title={
                      <Typography
                        variant="h6"
                      >
                        Job Order
                      </Typography>
                    }
                  />
                  <CardContent
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Grid container spacing={3}>
                          <Grid item xs={10}>
                            <TextField
                              label="Job Site Address"
                              fullWidth
                              value={
                                [
                                  jobOrder?.client?.address?.house_unit_street,
                                  jobOrder?.client?.address?.barangay,
                                  jobOrder?.client?.address?.city,
                                  jobOrder?.client?.address?.zipcode
                                ]
                                  .filter(Boolean)  // Filter out empty or undefined parts of the address
                                  .join(', ')       // Join them with commas
                              }
                              disabled
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              name="area"
                              label="Area"
                              value={jobOrder?.client?.address?.area || ''}
                              fullWidth
                              disabled
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={4}>
                        <TextField
                          label="Contact Person"
                          fullWidth
                          value={
                            [
                              jobOrder?.client?.contact?.lastname,
                              jobOrder?.client?.contact?.firstname
                            ]
                              .filter(Boolean) // Filter out empty/undefined values
                              .join(', ') || ''
                          }
                          disabled
                        />
                      </Grid>

                      <Grid item xs={4}>
                        <TextField
                          label="Contact Number"
                          fullWidth
                          value={
                            `${jobOrder?.client?.contact?.number?.type || ''}: ${jobOrder?.client?.contact?.number?.number || ''}`.trim() || ''
                          }
                          disabled
                        />
                      </Grid>

                      <Grid item xs={4}>
                        <TextField
                          label="Contact Email"
                          fullWidth
                          value={jobOrder?.client?.contact?.email?.email || ''}
                          disabled
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Competency Section */}
              <Grid
                item
                xs={12}
                md={12}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Card
                  variant="outlined"
                  sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                >
                  <CardHeader
                    title={
                      <Typography
                        variant="h6"
                      >
                        Competency
                      </Typography>
                    }
                  />
                  <CardContent>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
                        {competencies.map((option) => {
                          const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                          return (
                            <FormControlLabel
                              key={formattedOption}
                              control={
                                <Checkbox
                                  name={formattedOption}
                                  checked={jobOrder?.competencies?.includes(formattedOption)}
                                  disabled // Make checkbox read-only
                                />
                              }
                              label={option}
                            />
                          );
                        })}
                      </Box>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Specialization Section */}
              <Grid item xs={6} md={6}>
                <Card
                  variant="outlined"
                  sx={{ padding: "20px", marginBottom: "20px", marginRight: "5px" }}
                >
                  <CardHeader
                    title={
                      <Typography
                        variant="h6"
                      >
                        Specialization (General Pest Control)
                      </Typography>
                    }
                  />
                  <CardContent>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
                        {specializationGPC.map((option) => {
                          const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                          return (
                            <FormControlLabel
                              key={formattedOption}
                              control={
                                <Checkbox
                                  name={formattedOption}
                                  checked={jobOrder?.specialization?.general_pest_control?.includes(formattedOption)}
                                  disabled // Make checkbox read-only
                                />
                              }
                              label={option}
                            />
                          );
                        })}
                      </Box>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6} md={6}>
                <Card
                  variant="outlined"
                  sx={{ padding: "20px", marginBottom: "20px" }}
                >
                  <CardHeader
                    title={
                      <Typography
                        variant="h6"
                      >
                        Specialization (Termite)
                      </Typography>
                    }
                  />
                  <CardContent>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
                        {specializationTermite.map((option) => {
                          const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                          return (
                            <FormControlLabel
                              key={formattedOption}
                              control={
                                <Checkbox
                                  name={formattedOption}
                                  checked={jobOrder?.specialization?.termite?.includes(formattedOption)}
                                  disabled // Make checkbox read-only
                                />
                              }
                              label={option}
                            />
                          );
                        })}
                      </Box>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>


              {/* Schedule Section */}
              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{ padding: "20px", marginBottom: "20px" }}
                >
                  <CardHeader
                    title={
                      <Typography
                        variant="h6"
                      >
                        Schedule
                      </Typography>
                    }
                  />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={2}>
                        <TextField
                          label="Date"
                          name="job_date"
                          type="date"
                          value={jobOrder?.job_date || ''}
                          InputLabelProps={{ shrink: true, }}
                          disabled
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          label="Start Time"
                          name="start_time"
                          type="time"
                          value={jobOrder?.start_time || ''}
                          InputLabelProps={{ shrink: true, }}
                          disabled
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          label="End Time"
                          name="end_time"
                          type="time"
                          value={jobOrder?.end_time || ''}
                          InputLabelProps={{ shrink: true, }}
                          disabled
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

            </>
          )}

          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mb={2}
            mr={2}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate('/manage-leads')}
              sx={{
                fontSize: "0.875rem",
                color: "#FF4C4C",
                borderColor: "#FF4C4C",
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                marginLeft: "10px",
                fontSize: "0.875rem",
              }}
              disabled={isLoading}
            >
              Submit
            </Button>
          </Grid>

        </Grid>
      </form>


    </BaseFormWrapper>



    // <ThemeProvider theme={theme}>
    //   {/* Success Snackbar */}
    //   <Snackbar
    //     open={!!successMessage}
    //     autoHideDuration={3000}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //     onClose={() => setSuccessMessage("")}
    //   >
    //     <Alert
    //       severity="success"
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       {successMessage}
    //     </Alert>
    //   </Snackbar>

    //   {/* Loading Snackbar */}
    //   <Snackbar
    //     open={isLoading}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //   >
    //     <Alert
    //       severity="info"
    //       icon={<CircularProgress size={18} />}
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       Loading, please wait...
    //     </Alert>
    //   </Snackbar>

    //   {/* Error Snackbar */}
    //   <Snackbar
    //     open={!!errorMessage}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //     onClose={() => setErrorMessage("")}
    //   >
    //     <Alert
    //       severity="error"
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       {errorMessage}
    //     </Alert>
    //   </Snackbar>

    //   <Box sx={{ padding: "40px 60px" }}>
    //     <Card
    //       variant="outlined"
    //       style={{
    //         marginTop: "40px",
    //         marginBottom: "40px",
    //         borderRadius: 0,
    //       }}
    //     >
    //       <CardHeader
    //         title={
    //           <Box
    //             display="flex"
    //             alignItems="center"
    //             justifyContent="space-between"
    //           >
    //             <Box display="flex" alignItems="center">
    //               <TrackChangesIcon
    //                 sx={{
    //                   fontSize: "40px",
    //                   marginRight: "8px",
    //                 }}
    //               />
    //               <Typography variant="h6">Sales</Typography>
    //             </Box>
    //             <Breadcrumbs
    //               aria-label="breadcrumb"
    //               separator={<NavigateNextIcon fontSize="small" />}
    //               sx={{ color: "white" }}
    //             >
    //               <Typography sx={{ color: "#ffffff99" }}>
    //                 Sales
    //               </Typography>
    //               <Typography
    //                 component={RouterLink}
    //                 to="/manage-leads"
    //                 sx={{ color: "#ffffff99", textDecoration: "none" }}
    //               >
    //                 Manage Leads
    //               </Typography>
    //               <Typography sx={{ color: "#ffffff", textDecoration: "none" }}>
    //                 Edit Lead
    //               </Typography>
    //             </Breadcrumbs>
    //           </Box>
    //         }
    //         sx={{
    //           backgroundColor: "#1e5d45",
    //           color: "white",
    //           padding: "16px",
    //         }}
    //       />

    //       <CardContent sx={{ padding: "20px" }}>
    //         <Card variant="outlined" sx={{ padding: "20px" }}>
    //           <CardHeader
    //             title={
    //               <Typography variant="h5" sx={{ fontWeight: "bold" }}>
    //                 Edit Lead
    //               </Typography>
    //             }
    //             sx={{ marginBottom: 2 }}
    //           />

    //           <form onSubmit={handleSubmit} className="lead-form">
    //             <Grid container spacing={3}>
    //               {/* Client Information Section */}
    //               <Grid
    //                 item
    //                 xs={12}
    //                 md={12}
    //                 sx={{ display: "flex", flexDirection: "column" }}
    //               >
    //                 <Card
    //                   variant="outlined"
    //                   sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
    //                 >
    //                   <CardHeader
    //                     title={
    //                       <Typography
    //                         variant="h6"
    //                         sx={{
    //                           paddingBottom: "1rem",
    //                           fontWeight: "bold",
    //                           textTransform: "uppercase",
    //                           fontSize: "1rem",
    //                         }}
    //                       >
    //                         Client Information
    //                       </Typography>
    //                     }
    //                   />
    //                   <CardContent
    //                     sx={{
    //                       flex: 1,
    //                       display: "flex",
    //                       flexDirection: "column",
    //                     }}
    //                   >
    //                     <Grid container spacing={3}>
    //                       <Grid item xs={4}>
    //                         <TextField
    //                           label="Client Name"
    //                           name="clientName"
    //                           value={lead?.client?.name || ''}
    //                           fullWidth
    //                           multiline
    //                           disabled
    //                         />
    //                       </Grid>

    //                       {lead?.client?.addresses?.map((clientAddress, index) => (
    //                         <Grid item xs={12} key={index}>
    //                           <Grid container spacing={2}>
    //                             <Grid item xs={4}>
    //                               <TextField
    //                                 label="House/Unit Street"
    //                                 name={`house_unit_street ${index + 1}`}
    //                                 value={clientAddress.house_unit_street || ''}
    //                                 disabled
    //                                 fullWidth
    //                                 multiline
    //                               />
    //                             </Grid>
    //                             <Grid item xs={3}>
    //                               <TextField
    //                                 label="Barangay"
    //                                 value={clientAddress.barangay || ''}
    //                                 fullWidth
    //                                 disabled
    //                               />
    //                             </Grid>
    //                             <Grid item xs={3}>
    //                               <TextField
    //                                 label="City"
    //                                 name={`city ${index + 1}`}
    //                                 value={clientAddress.city || ''}
    //                                 fullWidth
    //                                 disabled
    //                               />
    //                             </Grid>
    //                             <Grid item xs={2}>
    //                               <TextField
    //                                 label="Zip Code"
    //                                 name={`zipcode ${index + 1}`}
    //                                 value={clientAddress.zipcode || ''}
    //                                 fullWidth
    //                                 disabled
    //                               />
    //                             </Grid>

    //                           </Grid>
    //                         </Grid>
    //                       ))}
    //                     </Grid>
    //                   </CardContent>
    //                 </Card>
    //               </Grid>

    //               {/* Contact Person Section */}
    //               <Grid
    //                 item
    //                 xs={12}
    //                 md={12}
    //                 sx={{ display: "flex", flexDirection: "column" }}
    //               >
    //                 <Card
    //                   variant="outlined"
    //                   sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
    //                 >
    //                   <CardHeader
    //                     title={
    //                       <Typography
    //                         variant="h6"
    //                         sx={{
    //                           paddingBottom: "1rem",
    //                           fontWeight: "bold",
    //                           textTransform: "uppercase",
    //                           fontSize: "1rem",
    //                         }}
    //                       >
    //                         Contact Person
    //                       </Typography>
    //                     }
    //                   />
    //                   <CardContent
    //                     sx={{
    //                       flex: 1,
    //                       display: "flex",
    //                       flexDirection: "column",
    //                     }}
    //                   >
    //                     {lead?.client?.contacts?.map((contact, index) => (
    //                       <Grid item xs={12} key={index}>
    //                         <Grid container spacing={3}>
    //                           <Grid item xs={12}>
    //                             <Grid container spacing={3}>
    //                               <Grid item xs={3}>
    //                                 <TextField
    //                                   label="First Name"
    //                                   fullWidth
    //                                   value={contact.firstname || ''}
    //                                   disabled
    //                                 />
    //                               </Grid>
    //                               <Grid item xs={3}>
    //                                 <TextField
    //                                   label="Last Name"
    //                                   fullWidth
    //                                   value={contact.lastname || ''}
    //                                   disabled
    //                                 />
    //                               </Grid>
    //                             </Grid>
    //                           </Grid>

    //                           {contact?.numbers?.map((number, numberIndex) => (
    //                             <React.Fragment key={numberIndex}>
    //                               <Grid item xs={3}>
    //                                 <TextField
    //                                   label="Contact Type"
    //                                   fullWidth
    //                                   value={
    //                                     number?.type === 'mobile'
    //                                       ? 'Mobile'
    //                                       : number?.type === 'landline'
    //                                         ? 'Landline'
    //                                         : ''
    //                                   }
    //                                   disabled
    //                                 />
    //                               </Grid>

    //                               <Grid item xs={3}>
    //                                 <TextField
    //                                   label="Contact Number"
    //                                   fullWidth
    //                                   value={number?.number || ''}
    //                                   disabled
    //                                 />
    //                               </Grid>

    //                             </React.Fragment>
    //                           ))}

    //                           {contact?.emails?.map((email, emailIndex) => (
    //                             <React.Fragment key={emailIndex}>
    //                               <Grid item xs={6}>
    //                                 <TextField
    //                                   label="Email Address"
    //                                   fullWidth
    //                                   value={email?.email || ''}
    //                                   disabled
    //                                 />
    //                               </Grid>
    //                             </React.Fragment>
    //                           ))}

    //                         </Grid>
    //                       </Grid>
    //                     ))}
    //                   </CardContent>
    //                 </Card>
    //               </Grid>

    //               <Grid
    //                 item
    //                 xs={12}
    //                 md={12}
    //                 sx={{ display: "flex", flexDirection: "column" }}
    //               >
    //                 <Card
    //                   variant="outlined"
    //                   sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
    //                 >
    //                   <CardHeader
    //                     title={
    //                       <Typography
    //                         variant="h6"
    //                         sx={{
    //                           paddingBottom: "1rem",
    //                           fontWeight: "bold",
    //                           textTransform: "uppercase",
    //                           fontSize: "1rem",
    //                         }}
    //                       >
    //                         Client Classifications
    //                       </Typography>
    //                     }
    //                   />
    //                   <CardContent
    //                     sx={{
    //                       flex: 1,
    //                       display: "flex",
    //                       flexDirection: "column",
    //                     }}
    //                   >
    //                     <Grid item xs={12}>
    //                       <Grid container spacing={2}>
    //                         {/* Category Field */}
    //                         <Grid item xs={2}>
    //                           <TextField
    //                             label="Category"
    //                             value={lead?.client?.category || ""}
    //                             fullWidth
    //                             InputProps={{ readOnly: true }}
    //                             variant="outlined"
    //                             sx={{ fontSize: "0.875rem" }}
    //                             disabled
    //                           />
    //                         </Grid>
    //                         {/* Parent Client Field */}
    //                         <Grid item xs={4}>
    //                           <TextField
    //                             label="Parent Client"
    //                             value={lead?.client?.parent_client?.name || ""}
    //                             fullWidth
    //                             InputProps={{ readOnly: true }}
    //                             variant="outlined"
    //                             sx={{
    //                               "& .MuiInputBase-input": { fontSize: "0.875rem" },
    //                               "& .MuiFormLabel-root": { fontSize: "0.875rem" },
    //                             }}
    //                             disabled
    //                           />
    //                         </Grid>
    //                         {/* Service Types */}
    //                         <Grid item xs={12}>
    //                           <Typography
    //                             variant="body2"
    //                             sx={{
    //                               paddingBottom: "0.5rem",
    //                               fontWeight: "normal",
    //                               textTransform: "none",
    //                               fontSize: "0.875rem",
    //                               color: "gray",
    //                             }}
    //                           >
    //                             Service Type
    //                           </Typography>
    //                           {competencies.map((option) => {
    //                             const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
    //                             return (
    //                               <FormControlLabel
    //                                 key={formattedOption}
    //                                 control={
    //                                   <Checkbox
    //                                     name={formattedOption}
    //                                     checked={
    //                                       Array.isArray(lead?.client?.service_types) &&
    //                                       lead.client.service_types.includes(formattedOption)
    //                                     }
    //                                   />
    //                                 }
    //                                 label={option}
    //                                 disabled
    //                               />
    //                             );
    //                           })}
    //                         </Grid>
    //                         {/* Location Type Field */}
    //                         <Grid item xs={2}>
    //                           <TextField
    //                             label="Location Type"
    //                             value={
    //                               lead?.client?.location_type
    //                                 ? lead.client.location_type.charAt(0).toUpperCase() + lead.client.location_type.slice(1).toLowerCase()
    //                                 : ""
    //                             }
    //                             fullWidth
    //                             InputProps={{ readOnly: true }}
    //                             variant="outlined"
    //                             sx={{ fontSize: "0.875rem" }}
    //                             disabled
    //                           />
    //                         </Grid>
    //                       </Grid>
    //                     </Grid>
    //                   </CardContent>
    //                 </Card>
    //               </Grid>


    //               {/* Sales Information Section */}
    //               <Grid
    //                 item
    //                 xs={12}
    //                 md={12}
    //                 sx={{ display: "flex", flexDirection: "column" }}
    //               >
    //                 <Card
    //                   variant="outlined"
    //                   sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
    //                 >
    //                   <CardHeader
    //                     title={
    //                       <Typography
    //                         variant="h6"
    //                         sx={{
    //                           paddingBottom: "1rem",
    //                           fontWeight: "bold",
    //                           textTransform: "uppercase",
    //                           fontSize: "1rem",
    //                         }}
    //                       >
    //                         Sales Information
    //                       </Typography>
    //                     }
    //                   />
    //                   <CardContent>
    //                     <Grid container spacing={3}>
    //                       <Grid item xs={12}>
    //                         <Grid container spacing={3}>
    //                           <Grid item xs={4}>
    //                             <TextField
    //                               label="Source Lead / Referred by"
    //                               name="sourceLeadreferredBy"
    //                               value={lead?.source_lead_referred_by || ''}  // Use optional chaining with fallback
    //                               onChange={(e) => handleLeadChange('source_lead_referred_by', e.target.value)}
    //                               fullWidth
    //                             />
    //                           </Grid>
    //                           <Grid item xs={4}>
    //                             <TextField
    //                               label="Payment Ref"
    //                               name="paymentRef"
    //                               value={lead?.payment_ref ?? ''}  // Use optional chaining with fallback
    //                               onChange={(e) => handleLeadChange('payment_ref', e.target.value)}
    //                               fullWidth
    //                             />
    //                           </Grid>
    //                           <Grid item xs={4}>
    //                             <TextField
    //                               label="Frequency of Service"
    //                               name="frequencyOfService"
    //                               value={lead?.frequency_of_service ?? ''}  // Fallback to empty string
    //                               onChange={(e) => handleLeadChange('frequency_of_service', e.target.value)}
    //                               fullWidth
    //                             />
    //                           </Grid>
    //                         </Grid>
    //                       </Grid>

    //                       <Grid item xs={12}>
    //                         <Grid container spacing={3}>
    //                           <Grid item xs={4}>
    //                             <FormControl fullWidth>
    //                               <InputLabel id="sales-rep-label">Sales Representative</InputLabel>
    //                               <Select
    //                                 labelId="sales-rep-label"
    //                                 value={selectedSalesRep ?? ''}  // Prevent undefined state
    //                                 onChange={handleSalesRepChange}
    //                                 label="Sales Representative"
    //                               >
    //                                 {salesReps.map((rep) => (
    //                                   <MenuItem key={rep.id} value={rep.id}>
    //                                     {`${rep.firstname} ${rep.lastname} - ${rep.branch}`}
    //                                   </MenuItem>
    //                                 ))}
    //                               </Select>
    //                             </FormControl>
    //                           </Grid>

    //                           <Grid item xs={2}>
    //                             <FormControl fullWidth>
    //                               <InputLabel id="status-label">Status</InputLabel>
    //                               <Select
    //                                 labelId="status-label"
    //                                 value={lead?.status ?? ''}  // Use nullish coalescing to handle undefined
    //                                 onChange={(e) => handleLeadChange('status', e.target.value)}
    //                                 label="Status"
    //                               >
    //                                 <MenuItem value="open">Open</MenuItem>
    //                                 <MenuItem value="contacted">Contacted</MenuItem>
    //                                 <MenuItem value="ocular">Ocular</MenuItem>
    //                                 <MenuItem value="closed">Closed</MenuItem>
    //                               </Select>
    //                             </FormControl>
    //                           </Grid>
    //                         </Grid>
    //                       </Grid>
    //                     </Grid>
    //                   </CardContent>
    //                 </Card>
    //               </Grid>

    //               <Grid
    //                 item
    //                 xs={12}
    //                 md={12}
    //                 sx={{ display: "flex", flexDirection: "column" }}
    //               >
    //                 <Card
    //                   variant="outlined"
    //                   sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
    //                 >
    //                   <CardHeader
    //                     title={
    //                       <Typography
    //                         variant="h6"
    //                         sx={{
    //                           paddingBottom: "1rem",
    //                           fontWeight: "bold",
    //                           textTransform: "uppercase",
    //                           fontSize: "1rem",
    //                         }}
    //                       >
    //                         Special Notes
    //                       </Typography>
    //                     }
    //                   />
    //                   <CardContent>
    //                     <Grid item xs={12}>
    //                       <TextField
    //                         label="Special Notes"
    //                         name="specialNotes"
    //                         value={lead?.special_notes ?? ''}  // Safe access to prevent undefined
    //                         onChange={(e) => handleLeadChange('special_notes', e.target.value)}
    //                         fullWidth
    //                         multiline
    //                         rows={4}
    //                       />
    //                     </Grid>
    //                   </CardContent>
    //                 </Card>
    //               </Grid>

    //               {/* Job Order Section */}
    //               {showJobOrder && ( // Conditionally render Job Order fields
    //                 <>
    //                   <Grid
    //                     item
    //                     xs={12}
    //                     md={12}
    //                     sx={{ display: "flex", flexDirection: "column" }}
    //                   >
    //                     <Card
    //                       variant="outlined"
    //                       sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
    //                     >
    //                       <CardHeader
    //                         title={
    //                           <Typography
    //                             variant="h6"
    //                             sx={{
    //                               paddingBottom: "1rem",
    //                               fontWeight: "bold",
    //                               textTransform: "uppercase",
    //                               fontSize: "1rem",
    //                             }}
    //                           >
    //                             Job Order
    //                           </Typography>
    //                         }
    //                       />
    //                       <CardContent
    //                         sx={{
    //                           flex: 1,
    //                           display: "flex",
    //                           flexDirection: "column",
    //                         }}
    //                       >
    //                         <Grid container spacing={3}>
    //                           <Grid item xs={12}>
    //                             <Grid container spacing={3}>
    //                               <Grid item xs={10}>
    //                                 <TextField
    //                                   label="Job Site Address"
    //                                   fullWidth
    //                                   value={
    //                                     [
    //                                       jobOrder?.client?.address?.house_unit_street,
    //                                       jobOrder?.client?.address?.barangay,
    //                                       jobOrder?.client?.address?.city,
    //                                       jobOrder?.client?.address?.zipcode
    //                                     ]
    //                                       .filter(Boolean)  // Filter out empty or undefined parts of the address
    //                                       .join(', ')       // Join them with commas
    //                                   }
    //                                   disabled
    //                                 />
    //                               </Grid>
    //                               <Grid item xs={2}>
    //                                 <TextField
    //                                   name="area"
    //                                   label="Area"
    //                                   value={jobOrder?.client?.address?.area || ''}
    //                                   fullWidth
    //                                   disabled
    //                                 />
    //                               </Grid>
    //                             </Grid>
    //                           </Grid>

    //                           <Grid item xs={4}>
    //                             <TextField
    //                               label="Contact Person"
    //                               fullWidth
    //                               value={
    //                                 [
    //                                   jobOrder?.client?.contact?.lastname,
    //                                   jobOrder?.client?.contact?.firstname
    //                                 ]
    //                                   .filter(Boolean) // Filter out empty/undefined values
    //                                   .join(', ') || ''
    //                               }
    //                               disabled
    //                             />
    //                           </Grid>

    //                           <Grid item xs={4}>
    //                             <TextField
    //                               label="Contact Number"
    //                               fullWidth
    //                               value={
    //                                 `${jobOrder?.client?.contact?.number?.type || ''}: ${jobOrder?.client?.contact?.number?.number || ''}`.trim() || ''
    //                               }
    //                               disabled
    //                             />
    //                           </Grid>

    //                           <Grid item xs={4}>
    //                             <TextField
    //                               label="Contact Email"
    //                               fullWidth
    //                               value={jobOrder?.client?.contact?.email?.email || ''}
    //                               disabled
    //                             />
    //                           </Grid>
    //                         </Grid>
    //                       </CardContent>
    //                     </Card>
    //                   </Grid>

    //                   {/* Competency Section */}
    //                   <Grid
    //                     item
    //                     xs={12}
    //                     md={12}
    //                     sx={{ display: "flex", flexDirection: "column" }}
    //                   >
    //                     <Card
    //                       variant="outlined"
    //                       sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
    //                     >
    //                       <CardHeader
    //                         title={
    //                           <Typography
    //                             variant="h6"
    //                             sx={{
    //                               paddingBottom: "1rem",
    //                               fontWeight: "bold",
    //                               textTransform: "uppercase",
    //                               fontSize: "1rem",
    //                             }}
    //                           >
    //                             Competency
    //                           </Typography>
    //                         }
    //                       />
    //                       <CardContent>
    //                         <Grid item xs={12}>
    //                           <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
    //                             {competencies.map((option) => {
    //                               const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
    //                               return (
    //                                 <FormControlLabel
    //                                   key={formattedOption}
    //                                   control={
    //                                     <Checkbox
    //                                       name={formattedOption}
    //                                       checked={jobOrder?.competencies?.includes(formattedOption)}
    //                                       disabled // Make checkbox read-only
    //                                     />
    //                                   }
    //                                   label={option}
    //                                 />
    //                               );
    //                             })}
    //                           </Box>
    //                         </Grid>
    //                       </CardContent>
    //                     </Card>
    //                   </Grid>

    //                   {/* Specialization Section */}
    //                   <Grid item xs={6} md={6}>
    //                     <Card
    //                       variant="outlined"
    //                       sx={{ padding: "20px", marginBottom: "20px" }}
    //                     >
    //                       <CardHeader
    //                         title={
    //                           <Typography
    //                             variant="h6"
    //                             sx={{
    //                               paddingBottom: "1rem",
    //                               fontWeight: "bold",
    //                               textTransform: "uppercase",
    //                               fontSize: "1rem",
    //                             }}
    //                           >
    //                             Specialization (General Pest Control)
    //                           </Typography>
    //                         }
    //                       />
    //                       <CardContent>
    //                         <Grid item xs={12}>
    //                           <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
    //                             {specializationGPC.map((option) => {
    //                               const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
    //                               return (
    //                                 <FormControlLabel
    //                                   key={formattedOption}
    //                                   control={
    //                                     <Checkbox
    //                                       name={formattedOption}
    //                                       checked={jobOrder?.specialization?.general_pest_control?.includes(formattedOption)}
    //                                       disabled // Make checkbox read-only
    //                                     />
    //                                   }
    //                                   label={option}
    //                                 />
    //                               );
    //                             })}
    //                           </Box>
    //                         </Grid>
    //                       </CardContent>
    //                     </Card>
    //                   </Grid>

    //                   <Grid item xs={6} md={6}>
    //                     <Card
    //                       variant="outlined"
    //                       sx={{ padding: "20px", marginBottom: "20px" }}
    //                     >
    //                       <CardHeader
    //                         title={
    //                           <Typography
    //                             variant="h6"
    //                             sx={{
    //                               paddingBottom: "1rem",
    //                               fontWeight: "bold",
    //                               textTransform: "uppercase",
    //                               fontSize: "1rem",
    //                             }}
    //                           >
    //                             Specialization (Termite)
    //                           </Typography>
    //                         }
    //                       />
    //                       <CardContent>
    //                         <Grid item xs={12}>
    //                           <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
    //                             {specializationTermite.map((option) => {
    //                               const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
    //                               return (
    //                                 <FormControlLabel
    //                                   key={formattedOption}
    //                                   control={
    //                                     <Checkbox
    //                                       name={formattedOption}
    //                                       checked={jobOrder?.specialization?.termite?.includes(formattedOption)}
    //                                       disabled // Make checkbox read-only
    //                                     />
    //                                   }
    //                                   label={option}
    //                                 />
    //                               );
    //                             })}
    //                           </Box>
    //                         </Grid>
    //                       </CardContent>
    //                     </Card>
    //                   </Grid>


    //                   {/* Schedule Section */}
    //                   <Grid item xs={12}>
    //                     <Card
    //                       variant="outlined"
    //                       sx={{ padding: "20px", marginBottom: "20px" }}
    //                     >
    //                       <CardHeader
    //                         title={
    //                           <Typography
    //                             variant="h6"
    //                             sx={{
    //                               paddingBottom: "1rem",
    //                               fontWeight: "bold",
    //                               textTransform: "uppercase",
    //                               fontSize: "1rem",
    //                             }}
    //                           >
    //                             Schedule
    //                           </Typography>
    //                         }
    //                       />
    //                       <CardContent>
    //                         <Grid container spacing={3}>
    //                           <Grid item xs={2}>
    //                             <TextField
    //                               label="Date"
    //                               name="job_date"
    //                               type="date"
    //                               value={jobOrder?.job_date || ''}
    //                               InputLabelProps={{ shrink: true, }}
    //                               disabled
    //                               fullWidth
    //                             />
    //                           </Grid>

    //                           <Grid item xs={2}>
    //                             <TextField
    //                               label="Start Time"
    //                               name="start_time"
    //                               type="time"
    //                               value={jobOrder?.start_time || ''}
    //                               InputLabelProps={{ shrink: true, }}
    //                               disabled
    //                               fullWidth
    //                             />
    //                           </Grid>

    //                           <Grid item xs={2}>
    //                             <TextField
    //                               label="End Time"
    //                               name="end_time"
    //                               type="time"
    //                               value={jobOrder?.end_time || ''}
    //                               InputLabelProps={{ shrink: true, }}
    //                               disabled
    //                               fullWidth
    //                             />
    //                           </Grid>
    //                         </Grid>
    //                       </CardContent>
    //                     </Card>
    //                   </Grid>

    //                 </>
    //               )}

    //               <Grid
    //                 item
    //                 xs={12}
    //                 display="flex"
    //                 justifyContent="flex-end"
    //                 alignItems="center"
    //                 mt={2}
    //                 mb={2}
    //                 mr={2}
    //               >
    //                 <Button
    //                   variant="outlined"
    //                   color="secondary"
    //                   onClick={() => navigate('/manage-leads')}
    //                   sx={{
    //                     fontSize: "0.875rem",
    //                     color: "#FF4C4C",
    //                     borderColor: "#FF4C4C",
    //                   }}
    //                   disabled={isLoading}
    //                 >
    //                   Cancel
    //                 </Button>
    //                 <Button
    //                   variant="contained"
    //                   color="primary"
    //                   type="submit"
    //                   sx={{
    //                     marginLeft: "10px",
    //                     fontSize: "0.875rem",
    //                   }}
    //                   disabled={isLoading}
    //                 >
    //                   Submit
    //                 </Button>
    //               </Grid>

    //             </Grid>
    //           </form>

    //         </Card>
    //       </CardContent>
    //     </Card>
    //   </Box>
    // </ThemeProvider>
  );
}

export default EditLead;
